import { motion } from "framer-motion";

/**
 * Reflections Auto Detailing — full lockup logo (R + "eflections" + tagline)
 * baked into a single transparent PNG, with rich animations layered on top.
 *
 *  - Bouncy entrance (scale + rotate spring)
 *  - Continuous gentle float
 *  - Focused sky-blue pulsing glow concentrated on the R (left side)
 *  - Chrome shine sweep across the whole lockup every few seconds
 *  - Three small orbital light particles around the R
 *  - Periodic sparkle bursts
 *
 *  Usage:
 *    <Logo />              → small icon-like rendering (auto-sized)
 *    <Logo showWordmark /> → full lockup at the requested size
 *
 *  `size` controls the overall height of the lockup.
 */
const Logo = ({
  size = 56,
  showWordmark = false,
  className = "",
  // eslint-disable-next-line no-unused-vars
  wordmarkClassName, // absorbed: wordmark is baked into PNG, don't forward to DOM
  ...rest
}) => {
  // The PNG aspect ratio is ~1200:484 ≈ 2.48:1
  const ASPECT = 1200 / 484;
  // When showing just the icon, size = height; for wordmark we use a taller height
  const height = showWordmark ? size * 1.4 : size;
  const width = height * ASPECT;

  // The R occupies roughly the first ~22% of the logo width.
  // We position a focused radial glow + orbital particles + sparkles over that region.
  const rCenterX = width * 0.16;
  const rCenterY = height * 0.5;
  const rRadius = height * 0.55;

  return (
    <motion.div
      className={`relative inline-block shrink-0 select-none ${className}`}
      style={{ width, height }}
      initial={{ opacity: 0, scale: 0.5, rotate: -90 }}
      animate={{
        opacity: 1,
        scale: 1,
        rotate: 0,
        y: [0, -3, 0],
      }}
      transition={{
        opacity: { duration: 0.5, ease: "easeOut" },
        scale: { duration: 0.9, ease: [0.34, 1.56, 0.64, 1] },
        rotate: { duration: 1.0, ease: [0.22, 0.9, 0.3, 1] },
        y: {
          duration: 4.2,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1.2,
        },
      }}
      whileHover={{ scale: 1.04 }}
      data-testid="logo-mark"
      {...rest}
    >
      {/* Pulsing sky glow focused on the R (left side of the lockup) */}
      <motion.div
        className="absolute pointer-events-none"
        style={{
          left: rCenterX - rRadius,
          top: rCenterY - rRadius,
          width: rRadius * 2,
          height: rRadius * 2,
          background:
            "radial-gradient(circle, rgba(14,165,233,0.6) 0%, rgba(14,165,233,0.25) 35%, transparent 70%)",
          filter: "blur(16px)",
        }}
        animate={{
          opacity: [0.35, 1, 0.35],
          scale: [0.9, 1.15, 0.9],
        }}
        transition={{ duration: 3.4, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Three light particles orbiting around the R */}
      <motion.div
        className="absolute pointer-events-none"
        style={{
          left: rCenterX - rRadius,
          top: rCenterY - rRadius,
          width: rRadius * 2,
          height: rRadius * 2,
        }}
        animate={{ rotate: 360 }}
        transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
      >
        {[0, 120, 240].map((deg) => (
          <span
            key={deg}
            className="absolute left-1/2 top-1/2 block w-1 h-1 rounded-full bg-sky-300"
            style={{
              boxShadow: "0 0 8px rgba(125,211,252,0.9)",
              transform: `translate(-50%, -50%) rotate(${deg}deg) translateY(-${
                rRadius * 0.9
              }px)`,
              opacity: 0.7,
            }}
          />
        ))}
      </motion.div>

      {/* The logo lockup itself */}
      <img
        src="/logo-full.png"
        alt="Reflections Auto Detailing"
        className="relative w-full h-full object-contain"
        style={{
          filter:
            "drop-shadow(0 0 6px rgba(14,165,233,0.35)) drop-shadow(0 0 14px rgba(14,165,233,0.18))",
        }}
        draggable={false}
      />

      {/* Chrome shine sweep across the whole logo */}
      <motion.img
        src="/logo-full.png"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-contain pointer-events-none"
        style={{
          filter:
            "brightness(2.2) drop-shadow(0 0 10px rgba(186,230,253,0.9))",
          mixBlendMode: "screen",
          WebkitMaskImage:
            "linear-gradient(110deg, transparent 35%, rgba(255,255,255,0.95) 48%, rgba(255,255,255,1) 50%, rgba(255,255,255,0.95) 52%, transparent 65%)",
          maskImage:
            "linear-gradient(110deg, transparent 35%, rgba(255,255,255,0.95) 48%, rgba(255,255,255,1) 50%, rgba(255,255,255,0.95) 52%, transparent 65%)",
          WebkitMaskSize: "300% 300%",
          maskSize: "300% 300%",
        }}
        animate={{
          WebkitMaskPosition: ["150% 150%", "-50% -50%"],
          maskPosition: ["150% 150%", "-50% -50%"],
        }}
        transition={{
          duration: 2.6,
          repeat: Infinity,
          ease: "easeInOut",
          repeatDelay: 1.6,
        }}
        draggable={false}
      />

      {/* Sparkle bursts twinkling near the R */}
      {[
        { x: rCenterX - rRadius * 0.4, y: rCenterY - rRadius * 0.55, delay: 0.5 },
        { x: rCenterX + rRadius * 0.35, y: rCenterY - rRadius * 0.3, delay: 1.9 },
        { x: rCenterX - rRadius * 0.2, y: rCenterY + rRadius * 0.55, delay: 3.3 },
      ].map((s, i) => (
        <motion.span
          key={i}
          className="absolute block w-1.5 h-1.5 rounded-full bg-white pointer-events-none"
          style={{
            left: s.x,
            top: s.y,
            boxShadow:
              "0 0 6px rgba(255,255,255,1), 0 0 12px rgba(14,165,233,0.85)",
          }}
          animate={{
            opacity: [0, 1, 0],
            scale: [0, 1.4, 0],
          }}
          transition={{
            duration: 1.4,
            delay: s.delay,
            repeat: Infinity,
            repeatDelay: 4.2,
            ease: "easeInOut",
          }}
        />
      ))}
    </motion.div>
  );
};

export default Logo;
